import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Upload as UploadIcon,
  Send as SendIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material';
import { EmailTemplate, CreateEmailTemplateRequest, SendEmailRequest } from '../../../types/emailTemplate.types';
import { apiService } from '../../../services/api';
import { useAppSelector } from '../../../redux/hooks';

interface EmailTemplateFormData {
  templateName: string;
  htmlFile: File | null;
  selectedTag: string;
}

interface EmailTemplateTag {
  id: number;
  tagName: string;
  templateId: string | null;
  templateName: string | null;
  hasTemplate: boolean;
  createdDate: string;
  modifiedDate: string;
}

interface SendEmailFormData {
  templateId: number;
  toEmail: string;
  fromEmail: string;
  subject: string;
  bodyElements: Record<string, string>;
  attachment?: File;
}

const EmailTemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [tags, setTags] = useState<EmailTemplateTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get current user from Redux
  const { user } = useAppSelector((state) => state.auth);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  
  // Form states
  const [formData, setFormData] = useState<EmailTemplateFormData>({
    templateName: '',
    htmlFile: null,
    selectedTag: '',
  });
  
  const [sendEmailData, setSendEmailData] = useState<SendEmailFormData>({
    templateId: 0,
    toEmail: '',
    fromEmail: '',
    subject: '',
    bodyElements: {},
  });
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getEmailTemplates();
      setTemplates(data);
      setError(null);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load tags
  const loadTags = useCallback(async () => {
    try {
      const data = await apiService.getEmailTemplateTags();
      setTags(data);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
    loadTags();
  }, [loadTemplates, loadTags]);

  // Show snackbar
  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      templateName: '',
      htmlFile: null,
      selectedTag: '',
    });
    setValidationErrors({});
  }, []);

  // Validate form
  const validateForm = useCallback((data: EmailTemplateFormData, isEdit: boolean = false): boolean => {
    const errors: Record<string, string> = {};

    if (!data.templateName.trim()) errors.templateName = 'Template name is required';
    if (!isEdit && !data.htmlFile) errors.htmlFile = 'HTML file is required';
    if (data.htmlFile && !data.htmlFile.name.toLowerCase().endsWith('.html')) {
      errors.htmlFile = 'Only HTML files are allowed';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, []);

  // Handle create template
  const handleCreateTemplate = useCallback(async () => {
    if (!validateForm(formData)) return;

    try {
      const createdTemplate = await apiService.createEmailTemplate({
        templateName: formData.templateName,
        htmlFile: formData.htmlFile!,
        createdBy: user ? `${user.firstName} ${user.lastName}` : 'Unknown User',
      });

      // If a tag is selected, assign the template to the tag
      if (formData.selectedTag) {
        try {
          await apiService.assignTemplateToTag(formData.selectedTag, createdTemplate.templateId, true);
          showSnackbar('Email template created and assigned to tag successfully');
        } catch (tagErr) {
          console.error('Error assigning template to tag:', tagErr);
          showSnackbar('Template created but failed to assign to tag', 'error');
        }
      } else {
        showSnackbar('Email template created successfully');
      }

      setCreateDialogOpen(false);
      resetForm();
      loadTemplates();
      loadTags(); // Reload tags to update the "Has Default" status
    } catch (err) {
      console.error('Error creating template:', err);
      showSnackbar('Failed to create email template', 'error');
    }
  }, [formData, validateForm, showSnackbar, resetForm, loadTemplates, loadTags]);

  // Handle edit template
  const handleEditTemplate = useCallback(async () => {
    if (!selectedTemplate || !validateForm(formData, true)) return;

    try {
      const updatedTemplate = await apiService.updateEmailTemplate(selectedTemplate.id, {
        templateName: formData.templateName,
        htmlFile: formData.htmlFile || undefined,
        modifiedBy: user ? `${user.firstName} ${user.lastName}` : 'Unknown User',
      });

      // Handle tag assignment changes
      const currentTag = selectedTemplate.tagName || '';
      const newTag = formData.selectedTag;

      if (currentTag !== newTag) {
        try {
          // If there was a previous tag, unassign it
          if (currentTag) {
            await apiService.unassignTemplateFromTag(currentTag);
          }

          // If a new tag is selected, assign the template to it
          if (newTag) {
            await apiService.assignTemplateToTag(newTag, selectedTemplate.templateId, true);
          }

          showSnackbar('Email template and tag assignment updated successfully');
        } catch (tagErr) {
          console.error('Error updating tag assignment:', tagErr);
          showSnackbar('Template updated but failed to update tag assignment', 'error');
        }
      } else {
        showSnackbar('Email template updated successfully');
      }

      setEditDialogOpen(false);
      resetForm();
      setSelectedTemplate(null);
      loadTemplates();
      loadTags(); // Reload tags to update the "Has Default" status
    } catch (err) {
      console.error('Error updating template:', err);
      showSnackbar('Failed to update email template', 'error');
    }
  }, [selectedTemplate, formData, validateForm, showSnackbar, resetForm, loadTemplates, loadTags]);

  // Handle delete template
  const handleDeleteTemplate = useCallback(async () => {
    if (!selectedTemplate) return;

    try {
      const success = await apiService.deleteEmailTemplate(selectedTemplate.id);
      if (!success) throw new Error('Failed to delete template');

      showSnackbar('Email template deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
      loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      showSnackbar('Failed to delete email template', 'error');
    }
  }, [selectedTemplate, showSnackbar, loadTemplates]);

  // Handle send email
  const handleSendEmail = useCallback(async () => {
    if (!selectedTemplate) return;

    try {
      await apiService.sendEmail({
        template: true,
        templateID: selectedTemplate.id,
        toEmail: sendEmailData.toEmail,
        fromEmail: sendEmailData.fromEmail,
        subject: sendEmailData.subject,
        bodyElements: sendEmailData.bodyElements,
        attachment: sendEmailData.attachment,
      });

      showSnackbar('Email sent successfully');
      setSendEmailDialogOpen(false);
      setSendEmailData({
        templateId: 0,
        toEmail: '',
        fromEmail: '',
        subject: '',
        bodyElements: {},
      });
    } catch (err) {
      console.error('Error sending email:', err);
      showSnackbar('Failed to send email', 'error');
    }
  }, [selectedTemplate, sendEmailData, showSnackbar]);

  // Open dialogs
  const openCreateDialog = useCallback(() => {
    resetForm();
    setCreateDialogOpen(true);
  }, [resetForm]);

  const openEditDialog = useCallback((template: EmailTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      templateName: template.templateName,
      htmlFile: null,
      selectedTag: template.tagName || '',
    });
    setEditDialogOpen(true);
  }, []);

  const openDeleteDialog = useCallback((template: EmailTemplate) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  }, []);

  const openSendEmailDialog = useCallback((template: EmailTemplate) => {
    setSelectedTemplate(template);
    const bodyElements: Record<string, string> = {};
    template.mappableFields.forEach(field => {
      bodyElements[field] = '';
    });
    setSendEmailData({
      templateId: template.id,
      toEmail: '',
      fromEmail: '',
      subject: '',
      bodyElements,
    });
    setSendEmailDialogOpen(true);
  }, []);

  const openPreviewDialog = useCallback((template: EmailTemplate) => {
    setSelectedTemplate(template);
    setPreviewDialogOpen(true);
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading email templates...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <EmailIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">{templates.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Email Templates
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CodeIcon color="secondary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">
                  {templates.reduce((sum, t) => sum + t.mappableFields.length, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Mappable Fields
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Stack>

      {/* Templates Table */}
      <Paper>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Email Templates ({templates.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Template
          </Button>
        </Box>
        <Divider />
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Template Name</TableCell>
                <TableCell>Mappable Fields</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Modified</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography variant="subtitle2">
                          {template.templateName}
                          {template.isTaggedAsDefault && (
                            <Tooltip title={`Default template for: ${template.tagName}`}>
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-block',
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: 'success.main',
                                  ml: 1,
                                }}
                              />
                            </Tooltip>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {template.templateId}
                          {template.tagName && (
                            <Chip 
                              label={template.tagName} 
                              size="small" 
                              variant="outlined" 
                              sx={{ ml: 1, fontSize: '0.6rem', height: 16 }} 
                            />
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {template.mappableFields.slice(0, 3).map((field) => (
                        <Chip
                          key={field}
                          label={field}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {template.mappableFields.length > 3 && (
                        <Chip
                          label={`+${template.mappableFields.length - 3} more`}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(template.createdDate).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {template.createdBy}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(template.modifiedDate).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {template.modifiedBy}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Preview Template">
                      <IconButton
                        size="small"
                        onClick={() => openPreviewDialog(template)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Send Email">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openSendEmailDialog(template)}
                      >
                        <SendIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Template">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(template)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Template">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(template)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Template Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Email Template</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Template Name"
              value={formData.templateName}
              onChange={(e) => setFormData(prev => ({ ...prev, templateName: e.target.value }))}
              error={!!validationErrors.templateName}
              helperText={validationErrors.templateName}
            />
            
            <FormControl fullWidth>
              <InputLabel>Email Tag (Optional)</InputLabel>
              <Select
                value={formData.selectedTag}
                label="Email Tag (Optional)"
                onChange={(e) => setFormData(prev => ({ ...prev, selectedTag: e.target.value }))}
              >
                <MenuItem value="">
                  <em>No tag selected</em>
                </MenuItem>
                {tags.map((tag) => (
                  <MenuItem key={tag.id} value={tag.tagName}>
                    {tag.tagName} {tag.hasTemplate && (
                      <Chip 
                        label="Has Default" 
                        size="small" 
                        color="warning" 
                        sx={{ ml: 1 }} 
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                HTML File {validationErrors.htmlFile && (
                  <Typography component="span" color="error" variant="caption">
                    - {validationErrors.htmlFile}
                  </Typography>
                )}
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ mb: 1 }}
              >
                {formData.htmlFile ? formData.htmlFile.name : 'Choose HTML File'}
                <input
                  type="file"
                  hidden
                  accept=".html"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setFormData(prev => ({ ...prev, htmlFile: file }));
                  }}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                Upload an HTML file with {`{{FieldName}}`} placeholders for dynamic content
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateTemplate} variant="contained">
            Create Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Email Template</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Template Name"
              value={formData.templateName}
              onChange={(e) => setFormData(prev => ({ ...prev, templateName: e.target.value }))}
              error={!!validationErrors.templateName}
              helperText={validationErrors.templateName}
            />

            <FormControl fullWidth>
              <InputLabel>Email Tag (Optional)</InputLabel>
              <Select
                value={formData.selectedTag}
                label="Email Tag (Optional)"
                onChange={(e) => setFormData(prev => ({ ...prev, selectedTag: e.target.value }))}
              >
                <MenuItem value="">
                  <em>No tag selected</em>
                </MenuItem>
                {tags.map((tag) => (
                  <MenuItem key={tag.id} value={tag.tagName}>
                    {tag.tagName} {tag.hasTemplate && tag.templateId !== selectedTemplate?.templateId && (
                      <Chip 
                        label="Has Default" 
                        size="small" 
                        color="warning" 
                        sx={{ ml: 1 }} 
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                HTML File (Optional - leave empty to keep current file)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ mb: 1 }}
              >
                {formData.htmlFile ? formData.htmlFile.name : 'Choose New HTML File'}
                <input
                  type="file"
                  hidden
                  accept=".html"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setFormData(prev => ({ ...prev, htmlFile: file }));
                  }}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                Upload a new HTML file to replace the current template content
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditTemplate} variant="contained">
            Update Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Template Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Email Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the email template "{selectedTemplate?.templateName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTemplate} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={sendEmailDialogOpen} onClose={() => setSendEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Email using Template</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="From Email"
                type="email"
                value={sendEmailData.fromEmail}
                onChange={(e) => setSendEmailData(prev => ({ ...prev, fromEmail: e.target.value }))}
              />
              <TextField
                fullWidth
                label="To Email"
                type="email"
                value={sendEmailData.toEmail}
                onChange={(e) => setSendEmailData(prev => ({ ...prev, toEmail: e.target.value }))}
              />
            </Stack>
            
            <TextField
              fullWidth
              label="Subject"
              value={sendEmailData.subject}
              onChange={(e) => setSendEmailData(prev => ({ ...prev, subject: e.target.value }))}
            />

            {selectedTemplate && selectedTemplate.mappableFields.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Template Fields
                </Typography>
                <Stack spacing={2}>
                  {selectedTemplate.mappableFields.map((field) => (
                    <TextField
                      key={field}
                      fullWidth
                      label={field}
                      value={sendEmailData.bodyElements[field] || ''}
                      onChange={(e) => setSendEmailData(prev => ({
                        ...prev,
                        bodyElements: {
                          ...prev.bodyElements,
                          [field]: e.target.value
                        }
                      }))}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Attachment (Optional)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
              >
                {sendEmailData.attachment ? sendEmailData.attachment.name : 'Choose File'}
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setSendEmailData(prev => ({ ...prev, attachment: file }));
                  }}
                />
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendEmailDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSendEmail} variant="contained" startIcon={<SendIcon />}>
            Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Template Preview</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Template Name
                </Typography>
                <Typography variant="body1">{selectedTemplate.templateName}</Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Mappable Fields ({selectedTemplate.mappableFields.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedTemplate.mappableFields.map((field) => (
                    <Chip key={field} label={field} variant="outlined" />
                  ))}
                </Box>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Template Information
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Created"
                      secondary={`${new Date(selectedTemplate.createdDate).toLocaleString()} by ${selectedTemplate.createdBy}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Last Modified"
                      secondary={`${new Date(selectedTemplate.modifiedDate).toLocaleString()} by ${selectedTemplate.modifiedBy}`}
                    />
                  </ListItem>
                </List>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailTemplateManagement;
